// Eigene Unterhaltungen des Nutzers: auflisten, lesen, umbenennen, löschen.
// Unterhaltungen sind privat – niemand sonst sieht sie, auch kein Administrator. Deshalb das Recht '@authenticated':
// Die eigenen Daten bleiben lesbar und löschbar, auch wenn das Recht „KI-Assistent nutzen“ später entzogen wird;
// neue Beiträge prüft die Laufzeit (assistant.chat.use, POST /api/assistant/…).
import { z } from 'zod'
import type { TranscriptItem } from '@shared/assistant'
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'

export const zConversation = z.object({
  id: zId,
  title: z.string().nullable(),
  provider: z.enum(['anthropic', 'mock', 'off']),
  model: z.string(),
  /** Skript-Anbieter: Antworten aus festen Beispielen, kein Sprachmodell */
  demo: z.boolean(),
  createdAt: z.string(),
  lastMessageAt: z.string().nullable(),
  /** Ende der Aufbewahrung – danach wird die Unterhaltung gelöscht */
  expiresAt: z.string().nullable(),
})

export const conversationList = defineContract({
  name: 'assistant.conversation.list',
  title: 'Unterhaltungen anzeigen',
  description: 'Listet die eigenen Unterhaltungen mit dem Assistenten, die jüngste zuerst.',
  llm: {
    description: 'Listet die Unterhaltungen des Nutzers mit dem Assistenten. Nur für die Oberfläche.',
    keywords: [],
  },
  input: z.strictObject({ limit: z.int().min(1).max(100).default(30) }),
  output: z.object({
    rows: z.array(
      zConversation.extend({ /** Vorschläge, die auf eine Entscheidung warten */ pendingProposals: z.int() }),
    ),
    /** es gibt ältere Unterhaltungen jenseits von `limit` */
    hasMore: z.boolean(),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
  assistant: 'never',
})

export const conversationGet = defineContract({
  name: 'assistant.conversation.get',
  title: 'Unterhaltung öffnen',
  description: 'Liefert eine eigene Unterhaltung mit ihrem Verlauf.',
  llm: {
    description: 'Liefert eine Unterhaltung des Nutzers mit dem Assistenten samt Verlauf. Nur für die Oberfläche.',
    keywords: [],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    conversation: zConversation,
    /**
     * Verlauf, gefaltet aus dem Ereignisprotokoll (applyAssistantEvent aus '@shared/assistant'). Die Oberfläche
     * übernimmt ihn als Ausgangszustand und öffnet den Strom mit ?after=<lastSeq>.
     */
    transcript: z.object({
      items: z.array(z.custom<TranscriptItem>((value) => typeof value === 'object' && value !== null)),
      lastSeq: z.int(),
      turn: z.object({ id: zId, status: z.enum(['running', 'awaiting_user']) }).nullable(),
    }),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
  assistant: 'never',
})

export const conversationRename = defineContract({
  name: 'assistant.conversation.rename',
  title: 'Unterhaltung umbenennen',
  description: 'Gibt einer eigenen Unterhaltung einen Titel.',
  llm: { description: 'Benennt eine Unterhaltung des Nutzers um. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    id: zId,
    title: z
      .string()
      .trim()
      .min(1, { error: 'Bitte geben Sie einen Titel ein.' })
      .max(80, { error: 'Der Titel darf höchstens 80 Zeichen haben.' }),
  }),
  output: z.object({ id: zId, title: z.string() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
  // persönlicher Zustand ohne Geschäftsbezug – Chatverläufe dienen der Bedienung, nicht der Kontrolle
  audit: 'none',
})

export const conversationDelete = defineContract({
  name: 'assistant.conversation.delete',
  title: 'Unterhaltung löschen',
  description:
    'Löscht eine eigene Unterhaltung mit Text und Verlauf. Über den Assistenten ausgeführte Buchungen und ihre Einträge im Audit-Protokoll bleiben bestehen.',
  llm: { description: 'Löscht eine Unterhaltung des Nutzers endgültig. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ id: zId }),
  output: z.object({ deleted: z.boolean() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
  audit: 'none',
})
